import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SCAN_PROFILES } from "@/lib/mockData";
import { Radar, Play, Target, Skull, Terminal, Cpu, Timer, ShieldCheck, Plus, Trash2, ListChecks, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLAN_TOOLS = {
  free: ["service_discovery"],
  standard: ["service_discovery", "vuln_scripts", "cve_correlation"],
  pro: ["service_discovery", "vuln_scripts", "cve_correlation", "nuclei", "nikto", "whatweb", "searchsploit", "subfinder", "httpx"],
};


const ANALYSIS = [
  { id: "service_discovery", name: "Découverte services & versions", desc: "nmap -sV — ports ouverts, services, versions", cat: "recon" },
  { id: "vuln_scripts", name: "Scan de vulnérabilités (NSE)", desc: "nmap --script vuln — détection de failles connues", cat: "vuln" },
  { id: "cve_correlation", name: "Corrélation CVE + CVSS", desc: "script vulners — associe services aux CVE connues", cat: "cve" },
  { id: "nuclei", name: "Nuclei — détection par templates", desc: "ProjectDiscovery · templates CVE/misconfig à jour (web & services)", cat: "nuclei" },
  { id: "nikto", name: "Nikto — scanner web", desc: "failles & mauvaises configs des serveurs web", cat: "web" },
  { id: "whatweb", name: "WhatWeb — empreinte techno", desc: "identifie technologies, CMS, frameworks, versions", cat: "recon" },
  { id: "searchsploit", name: "SearchSploit — exploits connus", desc: "recense les exploits publics (Exploit-DB) pour tes services · lecture seule", cat: "intel" },
  { id: "subfinder", name: "Subfinder — sous-domaines", desc: "énumère les sous-domaines d'un domaine autorisé (passif) · avant Nuclei", cat: "recon" },
  { id: "httpx", name: "httpx — sondage web", desc: "détecte les services web vivants + technologies (rapide) · avant Nuclei", cat: "recon" },
];

export default function Scanner() {
  const [profile, setProfile] = useState("standard");
  const [target, setTarget] = useState("127.0.0.1");
  const [opts, setOpts] = useState({ service_discovery: true, vuln_scripts: true, cve_correlation: true, nuclei: true, nikto: true, whatweb: true, searchsploit: true, subfinder: true, httpx: true });
  const [launching, setLaunching] = useState(false);
  const [allowlist, setAllowlist] = useState([]);
  const [newTarget, setNewTarget] = useState("");
  const [newNote, setNewNote] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const plan = (user && user.plan) || "free";
  const allowed = new Set(PLAN_TOOLS[plan] || PLAN_TOOLS.free);
  const eff = (id) => allowed.has(id) && !!opts[id];

  const currentProfile = SCAN_PROFILES.find(p => p.id === profile);
  const selectedCount = ANALYSIS.filter(a => allowed.has(a.id) && opts[a.id]).length;

  const loadAllowlist = () => api.getAllowlist().then(setAllowlist).catch(() => {});
  useEffect(() => { loadAllowlist(); }, []);

  const addToAllowlist = async () => {
    if (!newTarget.trim()) { toast.error("Entre une cible à autoriser"); return; }
    try {
      await api.addAllowlist(newTarget.trim(), newNote.trim());
      setNewTarget(""); setNewNote("");
      toast.success("Cible ajoutée à la liste blanche");
      loadAllowlist();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajout impossible");
    }
  };

  const removeFromAllowlist = async (id) => {
    await api.deleteAllowlist(id);
    loadAllowlist();
  };

  const toggleOpt = (id) => {
    if (!allowed.has(id)) { toast.message("Outil réservé", { description: "Passe à une formule supérieure pour débloquer cet outil." }); return; }
    setOpts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const launch = async () => {
    if (!target.trim()) { toast.error("Cible requise"); return; }
    if (selectedCount === 0) { toast.error("Active au moins une analyse autorisée"); return; }
    setLaunching(true);
    try {
      const sendOpts = {};
      ANALYSIS.forEach(a => { sendOpts[a.id] = allowed.has(a.id) && !!opts[a.id]; });
      const res = await api.createScan({ target: target.trim(), profile, ...sendOpts });
      toast.success(`Scan lancé — cible ${res.authorization}`, { description: `profil ${currentProfile.name}` });
      navigate(`/terminal?scan=${res.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan refusé", { duration: 7000 });
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="px-4 md:px-8 py-8 max-w-[1600px] mx-auto space-y-6 animate-fade-in" data-testid="scanner-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-primary tracking-widest mb-2">// MODULE 02 · RECON & SCAN</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Radar className="h-8 w-8 text-primary"/> Configure defensive scan
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Scan réel (nmap) sur tes réseaux privés ou cibles autorisées uniquement.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Target */}
          <Card className="bg-card/60 border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center"><Target className="h-4 w-4 text-primary"/></div>
              <div>
                <h3 className="font-semibold">Cible</h3>
                <p className="text-xs text-muted-foreground">IP, CIDR ou hostname — privé ou présent dans ta liste blanche</p>
              </div>
            </div>
            <Label className="font-mono text-[10px] tracking-widest text-muted-foreground">TARGET</Label>
            <Input data-testid="scan-target-input" value={target} onChange={e => setTarget(e.target.value)} placeholder="192.168.1.0/24 · 10.0.0.5 · localhost" className="font-mono mt-1.5 bg-background border-border focus-visible:ring-primary"/>
            <p className="text-[11px] text-muted-foreground mt-2 font-mono">// réseaux privés (10/8, 192.168/16, 172.16/12), loopback et cibles autorisées acceptés</p>
          </Card>

          {/* Allowlist */}
          <Card className="bg-card/60 border-border p-6" data-testid="allowlist-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-md bg-success/10 border border-success/30 flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-success"/></div>
              <div>
                <h3 className="font-semibold">Liste blanche — cibles que tu possèdes</h3>
                <p className="text-xs text-muted-foreground">Déclare ici les IP/domaines publics que tu es autorisé à scanner</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-2 mb-3">
              <Input data-testid="allowlist-value-input" value={newTarget} onChange={e => setNewTarget(e.target.value)} placeholder="mondomaine.com ou 203.0.113.10" className="font-mono bg-background border-border focus-visible:ring-primary"/>
              <Input data-testid="allowlist-note-input" value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="note (optionnel)" className="font-mono bg-background border-border focus-visible:ring-primary md:max-w-[180px]"/>
              <Button data-testid="allowlist-add-btn" onClick={addToAllowlist} className="gap-2 font-mono text-xs bg-success/90 hover:bg-success text-background"><Plus className="h-3.5 w-3.5"/>AUTORISER</Button>
            </div>
            <div className="space-y-2">
              {allowlist.length === 0 && <div className="text-xs text-muted-foreground font-mono">// aucune cible publique autorisée</div>}
              {allowlist.map(a => (
                <div key={a.id} data-testid={`allowlist-item-${a.value}`} className="flex items-center justify-between p-2.5 rounded-md border border-border bg-background">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-foreground truncate">{a.value}</div>
                    {a.note && <div className="text-[11px] text-muted-foreground truncate">{a.note}</div>}
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeFromAllowlist(a.id)}><Trash2 className="h-3.5 w-3.5"/></Button>
                </div>
              ))}
            </div>
          </Card>

          {/* Profile */}
          <Card className="bg-card/60 border-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center"><Skull className="h-4 w-4 text-primary"/></div>
              <div>
                <h3 className="font-semibold">Profil d'intensité</h3>
                <p className="text-xs text-muted-foreground">Timing nmap + étendue des ports scannés</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {SCAN_PROFILES.map(p => (
                <button key={p.id} data-testid={`profile-${p.id}`} onClick={() => setProfile(p.id)} className={cn(
                  "relative text-left p-4 rounded-md border transition-all",
                  profile === p.id ? "border-primary bg-primary/10 shadow-red-glow" : "border-border bg-background hover:border-primary/40"
                )}>
                  <div className="font-mono text-xs font-bold tracking-widest text-primary mb-1">{p.name}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed mb-3">{p.desc}</div>
                  <div className="flex gap-0.5">
                    {Array.from({length:6}).map((_,i)=>(<span key={i} className={cn("h-1.5 flex-1 rounded", i < p.speed ? "bg-primary" : "bg-muted")}/>))}
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Analysis modules */}
          <Card className="bg-card/60 border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center"><Cpu className="h-4 w-4 text-primary"/></div>
                <div>
                  <h3 className="font-semibold">Analyses à exécuter</h3>
                  <p className="text-xs text-muted-foreground">Modules nmap réels armés pour ce scan</p>
                </div>
              </div>
              <Badge variant="outline" className="font-mono text-[10px] border-primary/40 text-primary">{selectedCount} / {ANALYSIS.length} actifs</Badge>
            </div>
            <div className="space-y-2.5">
              {ANALYSIS.map(a => {
                const locked = !allowed.has(a.id);
                return (
                <label key={a.id} data-testid={`analysis-${a.id}`} className={cn(
                  "flex items-start gap-3 p-3 rounded-md border transition-colors",
                  locked ? "border-border bg-background/40 opacity-60 cursor-not-allowed"
                    : opts[a.id] ? "border-primary/40 bg-primary/5 cursor-pointer" : "border-border bg-background hover:border-primary/30 cursor-pointer"
                )}>
                  <Switch checked={!locked && opts[a.id]} disabled={locked} onCheckedChange={() => toggleOpt(a.id)}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold">{a.name}</span>
                      <Badge variant="outline" className="font-mono text-[9px] px-1.5 py-0 border-border text-muted-foreground uppercase">{a.cat}</Badge>
                      {locked && <Badge className="font-mono text-[9px] px-1.5 py-0 bg-primary/15 text-primary border-primary/30 gap-1"><Lock className="h-2.5 w-2.5"/>{["vuln_scripts","cve_correlation"].includes(a.id) ? "STANDARD" : "PRO"}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{a.desc}</p>
                  </div>
                </label>
              );})}
            </div>
            {plan !== "pro" && (
              <p className="text-[11px] text-muted-foreground mt-3 font-mono">// certains outils nécessitent la formule Pro — <a href="/pricing" className="text-primary hover:underline">voir les formules</a></p>
            )}
          </Card>
        </div>

        {/* Sticky summary */}
        <div className="lg:col-span-1">
          <Card className="bg-card/60 border-border p-6 sticky top-20">
            <div className="font-mono text-[10px] tracking-widest text-primary mb-1">// SCAN SUMMARY</div>
            <h3 className="font-bold text-lg mb-4">Prêt à scanner</h3>
            <div className="space-y-3 pb-4 border-b border-border">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Cible</span><span className="font-mono text-foreground truncate max-w-[140px]">{target || "—"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Profil</span><span className="font-mono text-primary font-bold">{currentProfile?.name}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Services</span><span className={cn("font-mono", eff("service_discovery") ? "text-primary" : "text-muted-foreground")}>{eff("service_discovery") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Vuln NSE</span><span className={cn("font-mono", eff("vuln_scripts") ? "text-primary" : "text-muted-foreground")}>{eff("vuln_scripts") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Corrélation CVE</span><span className={cn("font-mono", eff("cve_correlation") ? "text-primary" : "text-muted-foreground")}>{eff("cve_correlation") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Nuclei</span><span className={cn("font-mono", eff("nuclei") ? "text-primary" : "text-muted-foreground")}>{eff("nuclei") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Nikto</span><span className={cn("font-mono", eff("nikto") ? "text-primary" : "text-muted-foreground")}>{eff("nikto") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">WhatWeb</span><span className={cn("font-mono", eff("whatweb") ? "text-primary" : "text-muted-foreground")}>{eff("whatweb") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">SearchSploit</span><span className={cn("font-mono", eff("searchsploit") ? "text-primary" : "text-muted-foreground")}>{eff("searchsploit") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subfinder</span><span className={cn("font-mono", eff("subfinder") ? "text-primary" : "text-muted-foreground")}>{eff("subfinder") ? "ON" : "OFF"}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">httpx</span><span className={cn("font-mono", eff("httpx") ? "text-primary" : "text-muted-foreground")}>{eff("httpx") ? "ON" : "OFF"}</span></div>
            </div>
            <div className="py-4 space-y-2 border-b border-border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Timer className="h-3.5 w-3.5"/><span>Durée: <span className="text-foreground font-mono">variable (bornée à 4 min/hôte)</span></span></div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Terminal className="h-3.5 w-3.5"/><span>Sortie en direct dans Live Terminal</span></div>
            </div>
            <Button data-testid="launch-scan-btn" onClick={launch} disabled={launching} size="lg" className="w-full mt-4 bg-primary hover:bg-primary/90 text-primary-foreground font-mono tracking-widest gap-2 shadow-red-glow">
              <Play className="h-4 w-4"/> {launching ? "LANCEMENT…" : "LANCER LE SCAN"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center mt-3 font-mono">// cibles autorisées uniquement</p>

            <div className="mt-5 pt-5 border-t border-border">
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><ListChecks className="h-3 w-3"/> COMMAND PREVIEW</div>
              <div className="font-mono text-[11px] text-terminal bg-terminal border border-border rounded p-3 leading-relaxed break-all">
                <span className="text-primary">$</span> nmap -sT -Pn --open {profile === "stealth" ? "-T2" : profile === "standard" ? "-T3" : "-T4"} \\<br/>
                {eff("service_discovery") && <><span className="text-muted-foreground">  -sV</span> </>}
                {(eff("vuln_scripts") || eff("cve_correlation")) && <><span className="text-muted-foreground">--script {[eff("vuln_scripts") && "vuln", eff("cve_correlation") && "vulners"].filter(Boolean).join(",")}</span> </>}
                <br/><span className="text-muted-foreground">  {target || "<target>"}</span>
                {eff("nuclei") && <><br/><span className="text-primary">$</span> nuclei -u {target || "<target>"} <span className="text-muted-foreground">-severity low,medium,high,critical</span></>}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
